import type { DerivedSheet, ProficiencyView, StatisticView } from "../engine";
import { RANK_LABELS, signed } from "../engine";
import type { StatDetail } from "./stat-detail";
import { RollButton } from "./roll-button";
import { proficiencyDetail, statisticDetail } from "./stat-detail";
import { ListRow } from "./tiles";

interface Props {
  sheet: DerivedSheet;
  onStat: (detail: StatDetail) => void;
}

const RANK_SHORT = ["U", "T", "E", "M", "L"] as const;

/** The Skills tab (issue #14): Perception, every skill and lore with rank and modifier, a Roll button on each (#19), then the attack and defense proficiencies; each row opens its panel. */
export function Skills({ sheet, onStat }: Props) {
  const ctx = { sheet };
  const skills = Object.values(sheet.skills);
  const core = skills.filter((s) => !s.lore);
  const lores = skills.filter((s) => s.lore);
  const row = (s: StatisticView, selectors: string[]) => (
    <div key={s.slug} class={`list-row rollable${s.override ? " overridden" : ""}`}>
      <button type="button" class="row-open" onClick={() => onStat(statisticDetail(s, ctx, { selectors }))} title={`${s.label}: tap for the breakdown and Override`}>
        <span class="row-main">
          {s.label}
          {s.rank !== null && <span class={`tag rank rank-${s.rank}`} title={RANK_LABELS[s.rank]}>{RANK_SHORT[s.rank]}</span>}
          {s.override && <span class="stat-derived"> engine {signed(s.derived)}</span>}
        </span>
      </button>
      <RollButton class="roll-check" request={{ kind: "check", type: s.slug, statistic: s.slug, label: s.label, modifier: s.value, dc: null, against: null, basic: false, traits: [], options: [], name: null }}>
        <span class="roll-mod">{signed(s.value)}</span>
      </RollButton>
    </div>
  );
  const proficiency = (p: ProficiencyView) => (
    <ListRow key={p.slug} onTap={() => onStat(proficiencyDetail(p, ctx))} class={p.override ? "overridden" : ""}>
      <span class="row-main">
        {p.label}
        {p.custom ? <span class="tag">{p.sameAs ? `as ${p.sameAs}` : "custom"}</span> : null}
        {p.sourceName ? <span class="muted"> · {p.sourceName}</span> : null}
      </span>
      <span class="row-value">
        <span class="tag rank">{RANK_LABELS[p.rank]}</span> {signed(p.value)}
        {p.override && <span class="stat-derived"> engine {RANK_LABELS[p.derived]}</span>}
      </span>
    </ListRow>
  );
  return (
    <div class="skills">
      <section class="card list">
        <h3>Perception and skills</h3>
        {row(sheet.perception, ["perception", "wis-based"])}
        {core.map((s) => row(s, [s.slug, "skill-check", `${s.attribute ?? ""}-based`]))}
        {lores.length > 0 && <h4>Lore</h4>}
        {lores.map((s) => row(s, [s.slug, "skill-check", "lore", "int-based"]))}
        <p class="muted">U untrained, T trained, E expert, M master, L legendary. Tap a skill for its modifiers and an Override; tap its modifier to roll, hold to enter a physical die.</p>
      </section>
      <section class="card list">
        <h3>Attack proficiencies</h3>
        {Object.values(sheet.proficiencies.attacks).filter((p) => p.visible).map(proficiency)}
      </section>
      <section class="card list">
        <h3>Defense proficiencies</h3>
        {Object.values(sheet.proficiencies.defenses).filter((p) => p.visible).map(proficiency)}
      </section>
    </div>
  );
}
